import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { adminApi } from "./adminApi";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const input = "w-full rounded-2xl border border-[#1B3B28]/15 bg-[#FCFBF7] px-4 py-3 text-sm outline-none focus:border-[#3E6B48]";
const EMPTY = { title: "", description: "", consent: false, before: null, after: null };

const CaseForm = ({ onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.before || !f.after) return toast.error("Carica sia la foto prima sia quella dopo");
    if (!f.consent) return toast.error("Conferma di avere il consenso scritto del paziente");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try { await adminApi.post("/admin/cases", fd); toast.success("Caso pubblicato"); setF(EMPTY); e.target.reset(); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail?.toString() || "Caricamento non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-[#FCFBF7] border border-[#1B3B28]/10 p-6 grid gap-4 sm:grid-cols-2" data-testid="case-form">
      <input className={input} placeholder="Titolo (es. Parodontite stadio III)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required minLength={2} maxLength={90} data-testid="case-title-input" />
      <input className={input} placeholder="Descrizione breve (es. 1 seduta laser, controllo a 2 mesi)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={400} data-testid="case-description-input" />
      {["before", "after"].map((k) => (
        <label key={k} className="text-sm text-[#1B3B28]">
          <span className="eyebrow text-[#3E6B48]">{k === "before" ? "Foto prima" : "Foto dopo"}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setF({ ...f, [k]: e.target.files[0] || null })} className="mt-2 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#E6ECE4] file:px-4 file:py-2 file:text-[#1B3B28]" data-testid={`case-${k}-input`} />
        </label>
      ))}
      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#1B3B28]">
        <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} className="mt-0.5" data-testid="case-consent-checkbox" />
        Ho il consenso scritto del paziente alla pubblicazione delle foto, che non mostrano il volto né dati identificativi.
      </label>
      <button disabled={busy} className="sm:col-span-2 justify-self-start inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-6 py-3 text-sm hover:bg-[#3E6B48] disabled:opacity-60" data-testid="case-submit-btn">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Pubblica caso
      </button>
    </form>
  );
};

export const CasesTab = () => {
  const [cases, setCases] = useState([]);
  const load = () => adminApi.get("/cases").then(({ data }) => setCases(data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo caso dal sito?")) return;
    await adminApi.delete(`/admin/cases/${id}`); toast.success("Caso rimosso"); load();
  };
  return (
    <div data-testid="cases-tab">
      <p className="text-sm text-[#526356] max-w-2xl mb-6">Pubblica le foto prima e dopo la terapia laser: compaiono nella sezione "Casi reali" della homepage, con un cursore per confrontarle. La sezione resta nascosta finché non c'è almeno un caso.</p>
      <CaseForm onSaved={load} />
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cases.map((c) => (
          <div key={c.id} className="rounded-3xl bg-[#FCFBF7] border border-[#1B3B28]/10 p-4" data-testid={`case-item-${c.id}`}>
            <div className="grid grid-cols-2 gap-2">
              <img src={`${BACKEND}${c.before_url}`} alt="prima" className="aspect-square object-cover rounded-xl" />
              <img src={`${BACKEND}${c.after_url}`} alt="dopo" className="aspect-square object-cover rounded-xl" />
            </div>
            <div className="flex items-start justify-between gap-3 mt-3">
              <div><p className="f-serif text-xl text-[#1B3B28] leading-tight">{c.title}</p><p className="text-xs text-[#526356] mt-1">{c.description}</p></div>
              <button onClick={() => remove(c.id)} className="grid place-items-center w-9 h-9 shrink-0 rounded-full border border-[#1B3B28]/15 text-[#9A4B3C] hover:bg-[#F4E9E6]" aria-label="Rimuovi caso" data-testid={`case-remove-${c.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
